/**
 * registerValidation — click-time validation rules for the two RegisterPage signup doors.
 *
 * Always-clickable buttons (2026-08-04): the Sign Up and Continue with Google buttons no
 * longer disable themselves when the form is incomplete. Every condition that used to sit
 * inside `isSubmitDisabled` / the Google button's `blocked` expression now lives here and is
 * evaluated on CLICK, so the page can tell the parent what is missing instead of greying a
 * button out and leaving them to work it out.
 *
 * WHY A PURE MODULE: both doors share most of their rules, the ordering is load-bearing (see
 * below), and the rules are the one part of this change that is worth testing without
 * rendering the page. Nothing here touches React or the DOM.
 *
 * ORDERING IS THE CONTRACT. Each collector returns issues in the field's VISUAL order within
 * its card, because RegisterPage focuses `issues[0].fieldId` after the parent dismisses the
 * dialog. Return them out of order and focus lands on the wrong field, jumping the parent
 * past a problem they have not seen yet. Email card order is Email, Referral, Password,
 * Confirm, Consent; Google card order is Referral, Consent. Keep both in sync with the JSX.
 *
 * `fieldId` is the DOM id of the input to focus, and the two cards deliberately use different
 * ids for the same logical field (referralCode / googleReferralCode, tnc / googleTnc) because
 * both render on one page. Passing the wrong card's id focuses the other card.
 *
 * House style: no dashes in parent-facing copy.
 */

import { CODE_INVALID_MSG } from '@/hooks/useReferralField';

export interface RegisterValidationIssue {
  /** DOM id of the field to focus when this is the first issue. */
  fieldId: string;
  /** Parent-facing description of what is wrong. */
  message: string;
}

export interface EmailSignupValidationInput {
  email: string;
  password: string;
  confirmPassword: string;
  /** validatePassword(password).isValid — the page already computes it for PasswordRequirements. */
  isPasswordValid: boolean;
  /** useReferralField().isInvalid — the settled-invalid promo/referral gate. */
  codeInvalid: boolean;
  tncChecked: boolean;
}

export interface GoogleSignupValidationInput {
  /** useReferralField().isInvalid — the SAME gate as the email door (one code, one verdict). */
  codeInvalid: boolean;
  tncChecked: boolean;
  /**
   * DOM ids to focus. The Google card passes its own field ids; the EMAIL_EXISTS reverse-flow
   * CTA lives inside the EMAIL card and passes that card's ids, so the focus lands on the
   * fields the parent is actually looking at.
   */
  referralFieldId: string;
  tncFieldId: string;
}

/**
 * Email/password door. Returns [] when the form is ready to submit.
 *
 * The empty and malformed cases are separate issues on purpose: "Enter your email address"
 * and "Enter a valid email address" are different instructions, and merging them would tell a
 * parent who typed nothing that what they typed is wrong.
 */
export function collectEmailSignupIssues({
  email,
  password,
  confirmPassword,
  isPasswordValid,
  codeInvalid,
  tncChecked,
}: EmailSignupValidationInput): RegisterValidationIssue[] {
  const issues: RegisterValidationIssue[] = [];

  if (!email.trim()) {
    issues.push({ fieldId: 'email', message: 'Enter your email address.' });
  } else if (!email.includes('@')) {
    issues.push({ fieldId: 'email', message: 'Enter a valid email address.' });
  }

  // The referral code is OPTIONAL, so a blank field is never an issue. Only a code that has
  // settled as genuinely invalid blocks (promo-code US-005). An outage leaves the gate open.
  if (codeInvalid) {
    issues.push({ fieldId: 'referralCode', message: CODE_INVALID_MSG });
  }

  if (!password) {
    issues.push({ fieldId: 'password', message: 'Create a password.' });
  } else if (!isPasswordValid) {
    issues.push({
      fieldId: 'password',
      message: 'Your password does not meet all the requirements yet.',
    });
  }

  if (!confirmPassword) {
    issues.push({ fieldId: 'confirmPassword', message: 'Re-enter your password to confirm it.' });
  } else if (password !== confirmPassword) {
    issues.push({ fieldId: 'confirmPassword', message: 'Your two passwords do not match.' });
  }

  // US-002 / AC-1 / FR-G-001 / NFR-PDPA-001 / TO-LL-036: consent is a required affirmative
  // tick, and clicking an enabled button is NOT consent. This issue is what keeps the gate
  // real now that the button no longer carries it: handleSubmit refuses to call register()
  // while any issue is present, so an unticked box still cannot reach the API.
  if (!tncChecked) {
    issues.push({
      fieldId: 'tnc',
      message: 'Tick the box to agree to the Terms of Service and Privacy Policy.',
    });
  }

  return issues;
}

/**
 * Google door. Returns [] when the redirect may proceed.
 *
 * Only two rules, because the Google card asks for only two things. The same consent rule
 * applies for the same reason: ContinueWithGoogleButton captures the acceptance into
 * sessionStorage for the post-callback consent write, so it must never fire unticked.
 */
export function collectGoogleSignupIssues({
  codeInvalid,
  tncChecked,
  referralFieldId,
  tncFieldId,
}: GoogleSignupValidationInput): RegisterValidationIssue[] {
  const issues: RegisterValidationIssue[] = [];

  if (codeInvalid) {
    issues.push({ fieldId: referralFieldId, message: CODE_INVALID_MSG });
  }

  if (!tncChecked) {
    issues.push({
      fieldId: tncFieldId,
      message: 'Tick the box to agree to the Terms of Service and Privacy Policy.',
    });
  }

  return issues;
}
